import cn from "classnames";
import type { Plugin } from "app/lib/pipeline/plugins";
import * as HoverCard from "app/components/Playground/HoverCard";
import Wrapper from "../Wrapper";
import pluginDetails from "./pluginDetails";

interface Props {
  plugins: [string, Plugin][];
}

export default function Plugins({ plugins }: Props) {
  if (!plugins.length) {
    return null;
  }

  // A step might use the same plugin multiple times. Remove duplicate plugins and
  // sort them since we want to show the user the plugins being used, not
  // the number of times a plugin is used or how they're being configured.
  const distinctPlugins = Array.from(new Map(plugins).entries()).sort();

  return (
    <Wrapper title="Plugins" icon="custom/solid/20x/plug-solid">
      <ul className="flex flex-wrap gap-2">
        {distinctPlugins.map(([plugin], index) => {
          const [name, version = ""] = plugin.replace(/^.+?\//, "").split("#");
          const id = index + plugin;
          const payload = {
            id,
            content: (
              <HoverCard.Content id={id} label={pluginDetails[name]?.label || name}>
                <p>{pluginDetails[name]?.desc || version}</p>
                <p>
                  <a
                    href={`https://www.github.com/buildkite-plugins/${name}-buildkite-plugin`}
                    rel="noreferrer"
                    target="_blank"
                    className="text-purple-600 transition-colors duration-300 ease-in-out hover:text-purple-700"
                  >
                    View plugin repo ↗
                  </a>
                </p>
              </HoverCard.Content>
            ),
          };

          return (
            <li key={id}>
              <HoverCard.Trigger
                payload={payload}
                className={cn(
                  "bg-white",
                  [
                    "hover:bg-gray-100",
                    "focus:bg-gray-100",
                    "transition-colors",
                    "duration-300",
                    "ease-in-out",
                  ],
                  "cursor-help",
                  "inline-block",
                  ["px-3", "py-1"],
                  "rounded-lg",
                  "shadow-depth-200",
                  "space-x-1",
                  ["transition-shadow", "duration-300"],
                  "text-charcoal-300",
                  "text-sm",
                )}
              >
                <span className={cn(["text-charcoal-700"], "max-w-full truncate ...")}>{name}</span>
                {version && <span>{version}</span>}
              </HoverCard.Trigger>
            </li>
          );
        })}
      </ul>
    </Wrapper>
  );
}
